// Real Authentication API Service
// Handles authentication with the backend API using real user data

import { logger } from './logger'
import type { UserContext } from '@/types/api'

export interface AuthApiResponse<T> {
  data: T
  success: boolean
  message: string
  timestamp: string
  errors?: string[]
}

export class AuthApiService {
  private baseUrl: string

  constructor() {
    this.baseUrl = import.meta.env.VITE_API_BASE_URL || ''
  }

  async authenticateUser(userGuid: string): Promise<AuthApiResponse<UserContext>> {
    try {
      logger.info('Authenticating user with backend API', { userGuid })

      const response = await fetch(`${this.baseUrl}/api/auth/authenticate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ userGuid })
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      const apiResponse: AuthApiResponse<UserContext> = await response.json()

      if (!apiResponse.success) {
        throw new Error(apiResponse.message || 'Authentication failed')
      }

      logger.info('Successfully authenticated user', {
        userId: apiResponse.data.userId,
        tenantName: apiResponse.data.tenantName
      })

      return apiResponse
    } catch (error) {
      logger.error('Failed to authenticate user', { error, userGuid })
      throw error
    }
  }

  async authenticateWithHandoff(handoffToken: string): Promise<AuthApiResponse<UserContext>> {
    try {
      logger.info('Authenticating user with Next handoff token')

      const response = await fetch(`${this.baseUrl}/api/auth/handoff`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ handoffToken })
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      const apiResponse: AuthApiResponse<UserContext> = await response.json()

      if (!apiResponse.success) {
        throw new Error(apiResponse.message || 'Handoff authentication failed')
      }

      logger.info('Successfully authenticated handoff user', {
        userId: apiResponse.data.userId,
        tenantName: apiResponse.data.tenantName
      })

      return apiResponse
    } catch (error) {
      logger.error('Failed to authenticate handoff token', { error })
      throw error
    }
  }

  async getTenantBranding(tenantId: string): Promise<AuthApiResponse<any>> {
    try {
      logger.info('Getting tenant branding', { tenantId })

      const response = await fetch(`${this.baseUrl}/api/tenants/${encodeURIComponent(tenantId)}/branding`)

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      const apiResponse: AuthApiResponse<any> = await response.json()

      if (!apiResponse.success) {
        throw new Error(apiResponse.message || 'Failed to get tenant branding')
      }

      logger.info('Successfully retrieved tenant branding', { tenantId })

      return apiResponse
    } catch (error) {
      logger.error('Failed to get tenant branding', { error, tenantId })
      throw error
    }
  }
}

export const authApiService = new AuthApiService()
